/**
 * Palm Oracle New Reading Page
 * Handles photo capture/upload and analysis initiation
 * Phase 2 - Minimum Vertical Slice
 */

import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { 
  Camera, Upload, ArrowLeft, ArrowRight, Check, AlertCircle, 
  Loader2, Hand, RefreshCw, X, Info
} from 'lucide-react';
import { 
  createReading, uploadPalmImage, pollReadingStatus,
  HAND_OPTIONS, DOMINANT_HAND_OPTIONS, INTENT_OPTIONS, PACKAGE_OPTIONS
} from '../services/palmApi';
import { compressImage, needsCompression, formatBytes, getBase64Size } from '../utils/imageCompression';
import '../styles/palm.css';

const STEPS = {
  SETUP: 'setup',
  CAPTURE: 'capture',
  PREVIEW: 'preview',
  ANALYZING: 'analyzing',
  ERROR: 'error'
};

const PalmNewPage = ({ token, credits = 0, onCreditsUpdate, user }) => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const lang = i18n.language === 'en' ? 'en' : 'tr';
  
  // Wizard state
  const [step, setStep] = useState(STEPS.SETUP);
  
  // Form state
  const [selectedPackage, setSelectedPackage] = useState('standard');
  const [handSelection, setHandSelection] = useState('right');
  const [dominantHand, setDominantHand] = useState('right');
  const [intent, setIntent] = useState('general');
  const [question, setQuestion] = useState('');
  const [privacyConsent, setPrivacyConsent] = useState(true);
  
  // Image state - use Object URL for preview to reduce memory footprint
  const [capturedImage, setCapturedImage] = useState(null); // Base64 for upload
  const [imagePreview, setImagePreview] = useState(null);   // Object URL for display
  const previewUrlRef = useRef(null); // Track Object URL for cleanup
  
  // Camera state
  const [cameraActive, setCameraActive] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const fileInputRef = useRef(null);
  
  // Analysis state
  const [analysisProgress, setAnalysisProgress] = useState(null);
  const [error, setError] = useState(null);
  const [readingId, setReadingId] = useState(null);
  
  // Cleanup camera and Object URLs on unmount
  useEffect(() => {
    return () => {
      // Cleanup camera stream
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      // Cleanup Object URL to prevent memory leaks
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
      }
    };
  }, []);
  
  // Admin bypass check - admins have unlimited access
  const hasUnlimitedAccess = user?.is_admin === true || user?.role === 'admin';
  
  // Get credit cost for selected package
  const creditCost = PACKAGE_OPTIONS.find(p => p.value === selectedPackage)?.cost || 5;
  const hasEnoughCredits = hasUnlimitedAccess || credits >= creditCost;
  
  // Helper: Create Object URL from base64 for memory-efficient preview
  const createPreviewUrl = (base64Data) => {
    // Cleanup previous Object URL
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
    }
    
    // Convert base64 to blob for Object URL
    try {
      const byteString = atob(base64Data.split(',')[1] || base64Data);
      const mimeType = base64Data.match(/data:([^;]+);/)?.[1] || 'image/jpeg';
      const ab = new ArrayBuffer(byteString.length);
      const ia = new Uint8Array(ab);
      for (let i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i);
      }
      const blob = new Blob([ab], { type: mimeType });
      const objectUrl = URL.createObjectURL(blob);
      previewUrlRef.current = objectUrl;
      return objectUrl;
    } catch (e) {
      // Fallback to base64 if conversion fails
      console.warn('Object URL creation failed, using base64:', e);
      return base64Data;
    }
  };

  // ========== Camera Handling ==========
  const startCamera = async () => {
    try {
      setCameraError(null);
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { 
          facingMode: 'environment',
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }
      });
      
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      setCameraActive(true);
    } catch (err) {
      console.error('Camera error:', err);
      setCameraError(
        lang === 'tr' 
          ? 'Kameraya erişilemedi. Lütfen izin verin veya dosya yükleyin.'
          : 'Could not access camera. Please grant permission or upload a file.'
      );
    }
  };
  
  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setCameraActive(false);
  };
  
  const capturePhoto = async () => {
    if (!videoRef.current) return;
    
    const canvas = document.createElement('canvas');
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(videoRef.current, 0, 0);
    
    let imageData = canvas.toDataURL('image/jpeg', 0.85);
    
    // Compress if needed to prevent 520 errors
    if (needsCompression(imageData)) {
      try {
        console.log(`Camera capture needs compression: ${formatBytes(getBase64Size(imageData))}`);
        imageData = await compressImage(imageData);
      } catch (err) {
        console.error('Compression failed, using original:', err);
      }
    }
    
    // Store base64 for upload, create Object URL for preview (memory efficient)
    setCapturedImage(imageData);
    setImagePreview(createPreviewUrl(imageData));
    stopCamera();
    setStep(STEPS.PREVIEW);
  };
  
  // ========== File Upload Handling ==========
  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError(lang === 'tr' ? 'Lütfen geçerli bir resim dosyası seçin.' : 'Please select a valid image file.');
      return;
    }
    
    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError(lang === 'tr' ? 'Dosya boyutu 10MB\'dan küçük olmalı.' : 'File size must be less than 10MB.');
      return;
    }
    
    const reader = new FileReader();
    reader.onload = async (e) => {
      let imageData = e.target.result;
      
      // Compress if needed to prevent 520 errors on large images
      if (needsCompression(imageData)) {
        try {
          setAnalysisProgress({
            step: 'compressing',
            percent: 0,
            message: lang === 'tr' ? 'Görsel optimize ediliyor...' : 'Optimizing image...'
          });
          console.log(`File upload needs compression: ${formatBytes(getBase64Size(imageData))}`);
          imageData = await compressImage(imageData);
          setAnalysisProgress(null);
        } catch (err) {
          console.error('Compression failed, using original:', err);
          setAnalysisProgress(null);
        }
      }
      
      // Store base64 for upload, create Object URL for preview (memory efficient)
      setCapturedImage(imageData);
      setImagePreview(createPreviewUrl(imageData));
      setStep(STEPS.PREVIEW);
    };
    reader.readAsDataURL(file);
  };
  
  // ========== Analysis Flow ==========
  const startAnalysis = async () => {
    if (!capturedImage || !hasEnoughCredits) return;
    
    setStep(STEPS.ANALYZING);
    setError(null);
    setAnalysisProgress({
      step: 'creating',
      percent: 5,
      message: lang === 'tr' ? 'Okuma oluşturuluyor...' : 'Creating reading...'
    });
    
    try {
      // Step 1: Create reading
      const readingResponse = await createReading(token, {
        package: selectedPackage,
        handSelection,
        dominantHand,
        intent,
        question: question.trim() || null,
        privacyConsent
      });
      
      setReadingId(readingResponse.reading_id);
      
      setAnalysisProgress({
        step: 'uploading',
        percent: 15,
        message: lang === 'tr' ? 'Fotoğraf yükleniyor...' : 'Uploading photo...'
      });
      
      // Step 2: Upload image
      await uploadPalmImage(token, readingResponse.reading_id, capturedImage, handSelection);
      
      // Step 3: Poll for completion
      await pollReadingStatus(
        token,
        readingResponse.reading_id,
        (status) => {
          setAnalysisProgress({
            step: status.progress_step,
            percent: status.progress_percent || 50,
            message: lang === 'tr' ? status.progress_message_tr : status.progress_message_en
          });
        },
        60,  // max attempts
        2000 // interval ms
      );
      
      // Success - update credits and navigate to result
      if (onCreditsUpdate) {
        onCreditsUpdate();
      }
      
      navigate(`/palm/reading/${readingResponse.reading_id}`);
      
    } catch (err) {
      console.error('Analysis error:', err);
      setError(err.message);
      setStep(STEPS.ERROR);
    }
  };
  
  const retryCapture = () => {
    setCapturedImage(null);
    setImagePreview(null);
    setStep(STEPS.CAPTURE);
    startCamera();
  };
  
  const resetAll = () => {
    setCapturedImage(null);
    setImagePreview(null);
    setError(null);
    setReadingId(null);
    setAnalysisProgress(null);
    setStep(STEPS.SETUP);
  };
  
  // ========== Render Functions ==========
  
  const renderSetupStep = () => (
    <motion.div
      className="palm-setup"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
    >
      <h2 className="palm-page-title">
        {lang === 'tr' ? 'El Falı Ayarları' : 'Palm Reading Setup'}
      </h2>
      
      {/* Package Selection */}
      <div className="palm-form-section">
        <label className="palm-form-label">
          {lang === 'tr' ? 'Okuma Paketi' : 'Reading Package'}
        </label>
        <div className="palm-package-select">
          {PACKAGE_OPTIONS.map(pkg => (
            <button
              key={pkg.value}
              className={`palm-package-option ${selectedPackage === pkg.value ? 'selected' : ''} ${pkg.premium ? 'premium' : ''}`}
              onClick={() => setSelectedPackage(pkg.value)}
              disabled={pkg.value === 'oracle_fusion'} // Phase 4
            >
              <div className="package-option-header">
                <span className="package-name">{lang === 'tr' ? pkg.labelTr : pkg.labelEn}</span>
                <span className="package-cost">{pkg.cost} 💎</span>
              </div>
              <span className="package-desc">{lang === 'tr' ? pkg.descTr : pkg.descEn}</span>
              {selectedPackage === pkg.value && <Check size={18} className="package-check" />}
            </button>
          ))}
        </div>
      </div>
      
      {/* Hand Selection */}
      <div className="palm-form-section">
        <label className="palm-form-label">
          {lang === 'tr' ? 'Hangi Elinizi Okuyalım?' : 'Which Hand to Read?'}
        </label>
        <div className="palm-hand-select">
          {HAND_OPTIONS.map(opt => (
            <button
              key={opt.value}
              className={`palm-hand-option ${handSelection === opt.value ? 'selected' : ''}`}
              onClick={() => setHandSelection(opt.value)}
            >
              <span className="hand-icon">{opt.icon}</span>
              <span>{lang === 'tr' ? opt.labelTr : opt.labelEn}</span>
            </button>
          ))}
        </div>
      </div>
      
      {/* Dominant Hand */}
      <div className="palm-form-section">
        <label className="palm-form-label">
          {lang === 'tr' ? 'Baskın Eliniz?' : 'Your Dominant Hand?'}
        </label>
        <div className="palm-radio-group">
          {DOMINANT_HAND_OPTIONS.map(opt => (
            <label key={opt.value} className="palm-radio-option">
              <input
                type="radio"
                name="dominantHand"
                value={opt.value}
                checked={dominantHand === opt.value}
                onChange={(e) => setDominantHand(e.target.value)}
              />
              <span>{lang === 'tr' ? opt.labelTr : opt.labelEn}</span>
            </label>
          ))}
        </div>
      </div>
      
      {/* Intent Selection */}
      <div className="palm-form-section">
        <label className="palm-form-label">
          {lang === 'tr' ? 'Okuma Amacınız' : 'Reading Intent'}
        </label>
        <div className="palm-intent-grid">
          {INTENT_OPTIONS.map(opt => (
            <button
              key={opt.value}
              className={`palm-intent-option ${intent === opt.value ? 'selected' : ''}`}
              onClick={() => setIntent(opt.value)}
            >
              <span className="intent-icon">{opt.icon}</span>
              <span>{lang === 'tr' ? opt.labelTr : opt.labelEn}</span>
            </button>
          ))}
        </div>
      </div>
      
      {/* Optional Question */}
      <div className="palm-form-section">
        <label className="palm-form-label">
          {lang === 'tr' ? 'Sormak İstediğiniz Bir Soru (Opsiyonel)' : 'Question You Want to Ask (Optional)'}
        </label>
        <textarea
          className="palm-textarea"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          placeholder={lang === 'tr' ? 'Örn: Kariyerimde önemli bir değişiklik olacak mı?' : 'E.g.: Will there be a significant change in my career?'}
          maxLength={500}
        />
        <span className="palm-char-count">{question.length}/500</span>
      </div>
      
      {/* Privacy Consent */}
      <div className="palm-form-section">
        <label className="palm-checkbox-option">
          <input
            type="checkbox"
            checked={privacyConsent}
            onChange={(e) => setPrivacyConsent(e.target.checked)}
          />
          <span>
            {lang === 'tr' 
              ? 'Gizlilik politikasını kabul ediyorum ve fotoğrafımın analiz edilmesine izin veriyorum.'
              : 'I accept the privacy policy and allow my photo to be analyzed.'
            }
          </span>
        </label>
      </div>
      
      {/* Credit Warning */}
      {!hasEnoughCredits && (
        <div className="palm-warning">
          <AlertCircle size={20} />
          <span>
            {lang === 'tr' 
              ? `Yetersiz kredi. ${creditCost} kredi gerekli, mevcut: ${credits}`
              : `Insufficient credits. ${creditCost} credits needed, available: ${credits}`
            }
          </span>
        </div>
      )}
      
      {/* Continue Button */}
      <div className="palm-actions">
        <Link to="/palm" className="palm-btn palm-btn-secondary">
          <ArrowLeft size={18} />
          {lang === 'tr' ? 'Geri' : 'Back'}
        </Link>
        <button
          className="palm-btn palm-btn-primary"
          onClick={() => {
            setStep(STEPS.CAPTURE);
            startCamera();
          }}
          disabled={!privacyConsent || !hasEnoughCredits}
          data-testid="continue-to-capture"
        >
          {lang === 'tr' ? 'Devam Et' : 'Continue'}
          <ArrowRight size={18} />
        </button>
      </div>
    </motion.div>
  );
  
  const renderCaptureStep = () => (
    <motion.div
      className="palm-capture"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
    >
      <h2 className="palm-page-title">
        {lang === 'tr' ? 'Avuç İçi Fotoğrafı' : 'Palm Photo'}
      </h2>
      
      {/* Instructions */}
      <div className="palm-capture-instructions">
        <Info size={20} />
        <div>
          <p>
            {lang === 'tr' 
              ? 'Avuç içinizi düz tutun ve net bir fotoğraf çekin. İyi aydınlatma önemlidir.'
              : 'Hold your palm flat and take a clear photo. Good lighting is important.'
            }
          </p>
          <ul>
            <li>{lang === 'tr' ? 'Parmakları biraz açık tutun' : 'Keep fingers slightly spread'}</li>
            <li>{lang === 'tr' ? 'Gölge düşmemesine dikkat edin' : 'Avoid shadows on palm'}</li>
            <li>{lang === 'tr' ? 'Tüm avuç içi görünmeli' : 'Entire palm should be visible'}</li>
          </ul>
        </div>
      </div>
      
      {/* Camera/Preview Area */}
      <div className="palm-camera-container">
        {cameraError ? (
          <div className="palm-camera-error">
            <AlertCircle size={32} />
            <p>{cameraError}</p>
          </div>
        ) : cameraActive ? (
          <video
            ref={videoRef}
            autoPlay
            playsInline
            className="palm-camera-video"
          />
        ) : (
          <div className="palm-camera-placeholder">
            <Hand size={48} />
            <p>{lang === 'tr' ? 'Kamera başlatılıyor...' : 'Starting camera...'}</p>
          </div>
        )}
        
        {/* Overlay guide */}
        {cameraActive && (
          <div className="palm-camera-overlay">
            <div className="palm-guide-frame"></div>
            <span className="palm-guide-text">
              {lang === 'tr' ? 'Avuç içinizi çerçeveye yerleştirin' : 'Place your palm in the frame'}
            </span>
          </div>
        )}
      </div>
      
      {/* Capture/Upload Actions */}
      <div className="palm-capture-actions">
        {cameraActive && (
          <button
            className="palm-btn palm-btn-capture"
            onClick={capturePhoto}
            data-testid="capture-photo"
          >
            <Camera size={24} />
            {lang === 'tr' ? 'Fotoğraf Çek' : 'Take Photo'}
          </button>
        )}
        
        <span className="palm-or-divider">{lang === 'tr' ? 'veya' : 'or'}</span>
        
        <button
          className="palm-btn palm-btn-secondary"
          onClick={() => fileInputRef.current?.click()}
        >
          <Upload size={18} />
          {lang === 'tr' ? 'Dosya Yükle' : 'Upload File'}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />
      </div>
      
      {/* Back Button */}
      <div className="palm-actions">
        <button
          className="palm-btn palm-btn-ghost"
          onClick={() => {
            stopCamera();
            setStep(STEPS.SETUP);
          }}
        >
          <ArrowLeft size={18} />
          {lang === 'tr' ? 'Geri' : 'Back'}
        </button>
      </div>
    </motion.div>
  );
  
  const renderPreviewStep = () => (
    <motion.div
      className="palm-preview"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
    >
      <h2 className="palm-page-title">
        {lang === 'tr' ? 'Fotoğraf Önizleme' : 'Photo Preview'}
      </h2>
      
      {/* Image Preview */}
      <div className="palm-preview-container">
        <img 
          src={imagePreview} 
          alt="Palm preview" 
          className="palm-preview-image"
        />
      </div>
      
      {/* Confirmation */}
      <div className="palm-preview-info">
        <p>
          {lang === 'tr' 
            ? 'Fotoğraf net ve avuç içi iyi görünüyor mu?'
            : 'Is the photo clear and is the palm well visible?'
          }
        </p>
        <div className="palm-preview-details">
          <span>📦 {PACKAGE_OPTIONS.find(p => p.value === selectedPackage)?.[lang === 'tr' ? 'labelTr' : 'labelEn']}</span>
          <span>💎 {creditCost} {lang === 'tr' ? 'kredi' : 'credits'}</span>
        </div>
      </div>
      
      {/* Actions */}
      <div className="palm-actions palm-actions-stacked">
        <button
          className="palm-btn palm-btn-primary palm-btn-large"
          onClick={startAnalysis}
          data-testid="start-analysis"
        >
          <Check size={20} />
          {lang === 'tr' ? 'Evet, Analizi Başlat' : 'Yes, Start Analysis'}
        </button>
        <button
          className="palm-btn palm-btn-secondary"
          onClick={retryCapture}
        >
          <RefreshCw size={18} />
          {lang === 'tr' ? 'Hayır, Tekrar Çek' : 'No, Retake Photo'}
        </button>
      </div>
    </motion.div>
  );
  
  const renderAnalyzingStep = () => (
    <motion.div
      className="palm-analyzing"
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
    >
      <div className="palm-analyzing-content">
        <motion.div
          className="palm-analyzing-icon"
          animate={{ 
            rotate: [0, 360],
            scale: [1, 1.1, 1]
          }}
          transition={{ 
            rotate: { duration: 2, repeat: Infinity, ease: "linear" },
            scale: { duration: 1.5, repeat: Infinity }
          }}
        >
          ✋
        </motion.div>
        
        <h2>{lang === 'tr' ? 'Analiz Ediliyor...' : 'Analyzing...'}</h2>
        
        {analysisProgress && (
          <>
            <div className="palm-progress-bar">
              <motion.div 
                className="palm-progress-fill"
                initial={{ width: 0 }}
                animate={{ width: `${analysisProgress.percent}%` }}
                transition={{ duration: 0.5 }}
              />
            </div>
            <p className="palm-progress-message">{analysisProgress.message}</p>
            <p className="palm-progress-percent">{analysisProgress.percent}%</p>
          </>
        )}
        
        <p className="palm-analyzing-note">
          {lang === 'tr' 
            ? 'Bu işlem 30-60 saniye sürebilir. Lütfen bekleyin...'
            : 'This may take 30-60 seconds. Please wait...'
          }
        </p>
      </div>
    </motion.div>
  );
  
  const renderErrorStep = () => (
    <motion.div
      className="palm-error-page"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
    >
      <div className="palm-error-content">
        <AlertCircle size={48} className="palm-error-icon" />
        <h2>{lang === 'tr' ? 'Bir Hata Oluştu' : 'An Error Occurred'}</h2>
        <p className="palm-error-message">{error}</p>
        
        <div className="palm-actions palm-actions-stacked">
          <button
            className="palm-btn palm-btn-primary"
            onClick={retryCapture}
          >
            <RefreshCw size={18} />
            {lang === 'tr' ? 'Tekrar Dene' : 'Try Again'}
          </button>
          <button
            className="palm-btn palm-btn-secondary"
            onClick={resetAll}
          >
            <ArrowLeft size={18} />
            {lang === 'tr' ? 'Baştan Başla' : 'Start Over'}
          </button>
          <Link to="/palm" className="palm-btn palm-btn-ghost">
            {lang === 'tr' ? 'Ana Sayfaya Dön' : 'Back to Home'}
          </Link>
        </div>
      </div>
    </motion.div>
  );
  
  return (
    <div className="palm-container palm-new-page" data-testid="palm-new">
      {/* Back Navigation */}
      <div className="palm-nav">
        <Link to="/palm" className="palm-back-link">
          <ArrowLeft size={20} />
          <span>Palm Oracle</span>
        </Link>
        <div className="palm-credit-badge">
          <span className="credit-icon">💎</span>
          <span>{hasUnlimitedAccess ? '∞' : credits}</span>
        </div>
      </div>
      
      {/* Step Progress */}
      {step !== STEPS.ANALYZING && step !== STEPS.ERROR && (
        <div className="palm-step-indicator">
          <div className={`palm-step-dot ${step === STEPS.SETUP ? 'active' : step !== STEPS.SETUP ? 'completed' : ''}`}>1</div>
          <div className="palm-step-line" />
          <div className={`palm-step-dot ${step === STEPS.CAPTURE ? 'active' : step === STEPS.PREVIEW ? 'completed' : ''}`}>2</div>
          <div className="palm-step-line" />
          <div className={`palm-step-dot ${step === STEPS.PREVIEW ? 'active' : ''}`}>3</div>
        </div>
      )}
      
      {/* Content */}
      <AnimatePresence mode="wait">
        {step === STEPS.SETUP && renderSetupStep()}
        {step === STEPS.CAPTURE && renderCaptureStep()}
        {step === STEPS.PREVIEW && renderPreviewStep()}
        {step === STEPS.ANALYZING && renderAnalyzingStep()}
        {step === STEPS.ERROR && renderErrorStep()}
      </AnimatePresence>
    </div>
  );
};

export default PalmNewPage;
